import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import useFetch from '../../hooks/useFetch';

import { axiosInstance } from '../../config/axios';

// ─── Styled components ─────────────────────────────────────────────────────

const Overlay = styled.div`
	position: fixed;
	top: 0; left: 0; right: 0; bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
`;

const Dialog = styled.div`
	background: white;
	border-radius: 12px;
	max-width: 480px;
	width: 100%;
	max-height: 90vh;
	overflow-y: auto;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px 24px 16px;
	border-bottom: 1px solid #e9ecef;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 1.4em;
	color: #2c3e50;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 1.5em;
	cursor: pointer;
	color: #666;
	padding: 0;
	line-height: 1;
	&:hover { color: #333; }
`;

const Body = styled.div`
	padding: 24px;
`;

const FieldGroup = styled.div`
	margin-bottom: 20px;
`;

const Label = styled.label`
	display: block;
	font-weight: 600;
	font-size: 0.9em;
	color: #2c3e50;
	margin-bottom: 6px;
`;

const Select = styled.select`
	width: 100%;
	padding: 10px 14px;
	border: 1px solid #ddd;
	border-radius: 6px;
	font-size: 1em;
	background: white;
	cursor: pointer;
	transition: border-color 0.2s ease;
	&:hover { border-color: #3498db; }
	&:focus { outline: none; border-color: #3498db; box-shadow: 0 0 0 2px rgba(52,152,219,0.2); }
`;

const Input = styled.input`
	width: 100%;
	padding: 10px 14px;
	border: 1px solid #ddd;
	border-radius: 6px;
	font-size: 1em;
	box-sizing: border-box;
	&:focus { outline: none; border-color: #3498db; box-shadow: 0 0 0 2px rgba(52,152,219,0.2); }
`;

const Actions = styled.div`
	display: flex;
	gap: 12px;
	padding: 0 24px 24px;
`;

const SubmitButton = styled.button<{ $submitted?: boolean }>`
	flex: 1;
	padding: 12px 20px;
	border-radius: 6px;
	font-size: 1em;
	cursor: pointer;
	border: none;
	font-weight: 600;
	transition: all 0.2s ease;
	background: ${({ $submitted }) => ($submitted ? '#27ae60' : '#3498db')};
	color: white;
	&:hover { background: ${({ $submitted }) => ($submitted ? '#219a52' : '#2980b9')}; }
	&:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelButton = styled.button`
	padding: 12px 20px;
	border-radius: 6px;
	font-size: 1em;
	cursor: pointer;
	border: 1px solid #ddd;
	background: white;
	color: #666;
	font-weight: 500;
	transition: all 0.2s ease;
	&:hover { background: #f8f9fa; border-color: #ccc; }
`;

const SubmittedBadge = styled.div`
	background: #d4edda;
	border: 1px solid #c3e6cb;
	border-radius: 6px;
	padding: 10px 14px;
	margin-bottom: 20px;
	color: #155724;
	font-size: 0.9em;
	text-align: center;
`;

const ErrorMessage = styled.div`
	background: #f8d7da;
	border: 1px solid #f5c6cb;
	border-radius: 6px;
	padding: 10px 14px;
	margin-bottom: 16px;
	color: #721c24;
	font-size: 0.9em;
	text-align: center;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────

// School years from 2000-2001 up to the current school year.
const generateSchoolYears = (): string[] => {
	const years: string[] = [];
	const now = new Date();
	const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
	for (let y = startYear; y >= 2000; y--) {
		years.push(`${y}-${y + 1}`);
	}
	return years;
};

const getFingerprint = (): string => {
	const key = 'aeig_year_suggest_fingerprint';
	let fp = localStorage.getItem(key);
	if (!fp) {
		fp = crypto.randomUUID
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		localStorage.setItem(key, fp);
	}
	return fp;
};

// ─── Props ─────────────────────────────────────────────────────────────────

interface YearSuggestDialogProps {
	albumId: string;
	albumName: string;
	onClose: () => void;
}

// ─── Component ─────────────────────────────────────────────────────────────

const YearSuggestDialog: React.FC<YearSuggestDialogProps> = ({
	albumId,
	albumName,
	onClose,
}) => {
	const queryClient = useQueryClient();
	const schoolYears = useMemo(() => generateSchoolYears(), []);
	const fingerprint = useMemo(() => getFingerprint(), []);

	const [schoolYear, setSchoolYear] = useState(schoolYears[0] || '');
	const [respondentLabel, setRespondentLabel] = useState('');
	const [submitted, setSubmitted] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const { data: myData } = useFetch<{
		suggestion: { schoolYear: string; respondentLabel?: string } | null;
	}>({
		queryKey: ['my-year-suggestion', albumId, fingerprint],
		url: `/api/galeria/albums/${albumId}/my-year-suggestion?fingerprint=${encodeURIComponent(fingerprint)}`,
	});

	useEffect(() => {
		if (myData?.suggestion) {
			setSchoolYear(myData.suggestion.schoolYear);
			if (myData.suggestion.respondentLabel) {
				setRespondentLabel(myData.suggestion.respondentLabel);
			}
			setSubmitted(myData.suggestion.schoolYear);
		}
	}, [myData]);

	const handleSubmit = useCallback(async () => {
		if (!schoolYear) {
			setError('Selecciona un curs');
			return;
		}
		setIsSubmitting(true);
		setError(null);
		try {
			const payload: Record<string, unknown> = { schoolYear, fingerprint };
			if (respondentLabel.trim()) {
				payload.respondentLabel = respondentLabel.trim();
			}
			await axiosInstance.post(
				`/api/galeria/albums/${albumId}/year-suggest`,
				payload,
			);
			queryClient.invalidateQueries({
				queryKey: ['my-year-suggestion', albumId, fingerprint],
			});
			// The pending count on the admin overview and the album's own state
			// both change with a new suggestion — don't leave them stale.
			queryClient.invalidateQueries({
				queryKey: ['year-suggestions-overview'],
			});
			queryClient.invalidateQueries({ queryKey: ['album', albumId] });
			queryClient.invalidateQueries({ queryKey: ['albums'] });
			setSubmitted(schoolYear);
		} catch (err: any) {
			setError(
				err?.response?.data?.error || 'Error en enviar el suggeriment',
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [schoolYear, fingerprint, respondentLabel, albumId, queryClient]);

	return (
		<Overlay onClick={onClose}>
			<Dialog onClick={(e) => e.stopPropagation()}>
				<Header>
					<Title>Quin any és aquest àlbum?</Title>
					<CloseButton onClick={onClose}>&times;</CloseButton>
				</Header>
				<Body>
					<p style={{ margin: '0 0 16px', color: '#555', fontSize: '0.95em' }}>
						<strong>{albumName}</strong>
					</p>

					{submitted && (
						<SubmittedBadge>
							✔ Gràcies! Has suggerit <strong>{submitted}</strong>. Pots canviar-ho si cal.
						</SubmittedBadge>
					)}

					{error && <ErrorMessage>{error}</ErrorMessage>}

					<FieldGroup>
						<Label htmlFor="ys-year">Curs escolar</Label>
						<Select
							id="ys-year"
							value={schoolYear}
							onChange={(e) => setSchoolYear(e.target.value)}
						>
							{schoolYears.map((year) => (
								<option key={year} value={year}>
									{year}
								</option>
							))}
						</Select>
					</FieldGroup>

					<FieldGroup>
						<Label htmlFor="ys-name">El teu nom (opcional)</Label>
						<Input
							id="ys-name"
							type="text"
							placeholder="Ex: Maria"
							value={respondentLabel}
							onChange={(e) => setRespondentLabel(e.target.value)}
						/>
					</FieldGroup>
				</Body>
				<Actions>
					<CancelButton onClick={onClose}>Cancel·lar</CancelButton>
					<SubmitButton
						$submitted={!!submitted}
						onClick={handleSubmit}
						disabled={isSubmitting}
					>
						{isSubmitting
							? 'Enviant...'
							: submitted
							? 'Actualitzar'
							: 'Envia'}
					</SubmitButton>
				</Actions>
			</Dialog>
		</Overlay>
	);
};

export default YearSuggestDialog;